<template>
  <div class="authLayout">
    <div class="authLayout_wrapper">
      <div class="authLayout_preHeader"><slot name="preHeader" /></div>
      <div class="authLayout_header">
        <div class="authLayout_header_logo">
          <component :is="componentLogo" />
        </div>
        <div class="authLayout_header_title"><slot name="title" /></div>
        <div class="authLayout_header_description"><slot name="description" /></div>
      </div>

      <div class="authLayout_content">
        <slot name="content" />
      </div>
      <div v-if="showAgreement" class="authLayout_agreement">
        {{ $t('auth.agreement.part1') }} <a class="authLayout_agreement_link" :href="termsAgreementLink">{{ $t('auth.agreement.part2') }}</a> {{ $t('auth.agreement.part3') }} <a class="authLayout_agreement_link" :href="personalDataAgreementLink">{{ $t('auth.agreement.part4') }}</a>
      </div>

      <template v-if="showSupport">
        <mq-responsive target="mobile">
          <div class="authLayout_support -mobile">
            <div class="authLayout_support_tip">{{ $t('auth.needHelp') }}</div>
            <div class="authLayout_support_link" @click="handlerSupportButton">
              {{ $t('contactSupport') }}
            </div>
          </div>
        </mq-responsive>

        <mq-responsive target="tablet-desktop">
          <div class="authLayout_support" @click="handlerSupportButton">
            <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="52" height="52" rx="26" fill="#A990E2"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M37.4283 23.452C36.9854 19 34.2997 12 25.9999 12C17.7 12 15.0143 19 14.5714 23.452C13.0179 24.0298 11.9931 25.4916 12 27.12V29.08C12 31.245 13.7909 33 16 33C18.2091 33 20 31.2449 20 29.08V27.12C19.9927 25.5267 19.0057 24.095 17.4999 23.494C17.7857 20.918 19.1857 14.8 25.9999 14.8C32.8141 14.8 34.1998 20.918 34.4855 23.494C32.9828 24.0963 32.0007 25.529 31.9998 27.12V29.08C32.0029 29.8176 32.2175 30.5395 32.6191 31.1633C33.0207 31.7871 33.5932 32.2877 34.2712 32.608C33.6712 33.714 32.1426 35.212 28.3855 35.66C27.6345 34.5424 26.1815 34.1018 24.9176 34.6084C23.6538 35.1151 22.9324 36.4274 23.196 37.7404C23.4596 39.0534 24.6344 40 25.9999 40C26.529 39.9971 27.0469 39.8503 27.4958 39.5759C27.9448 39.3015 28.3072 38.9104 28.5427 38.446C34.6712 37.76 36.6283 34.666 37.2426 32.846C38.9044 32.3184 40.0221 30.7918 39.9997 29.08V27.12C40.0066 25.4916 38.9818 24.0298 37.4283 23.452ZM17.1428 29.08C17.1428 29.6985 16.6312 30.2 16 30.2C15.3688 30.2 14.8572 29.6986 14.8572 29.08V27.12C14.856 26.9722 14.8847 26.8256 14.9416 26.6887C14.9985 26.5519 15.0825 26.4274 15.1888 26.3225C15.295 26.2176 15.4214 26.1343 15.5606 26.0775C15.6998 26.0206 15.8492 25.9914 16 25.9914C16.1508 25.9914 16.3001 26.0206 16.4394 26.0775C16.5786 26.1343 16.705 26.2176 16.8112 26.3225C16.9174 26.4274 17.0014 26.5519 17.0584 26.6887C17.1153 26.8256 17.144 26.9722 17.1428 27.12V29.08ZM34.8569 27.12C34.8569 26.5014 35.3685 26 35.9997 26C36.6309 26 37.1426 26.5014 37.1426 27.12V29.08C37.1426 29.6985 36.6309 30.2 35.9997 30.2C35.3685 30.2 34.8569 29.6986 34.8569 29.08V27.12Z" fill="white"/>
            </svg>
          </div>
        </mq-responsive>
      </template>
    </div>

    <ac-modal v-if="isShowSupportModal" v-model="isShowSupportModal" mobile-modal-type="bottomSheet">
      <auth-support-modal-content @close="isShowSupportModal = false" />
    </ac-modal>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { MqResponsive } from 'vue3-mq'

import AcModal from '@ui-kit/components/AcModal.vue'
import AuthSupportModalContent from '@web/platform.v3/components/auth/support-modal-content.vue'
import AnyLogo from '@web/platform.v3/components/AnyLogo.vue'
import TakeLogo from '@web/platform.v3/components/TakeLogo.vue'

import getBaseUrl from '@web/common/getBaseUrl'
import { specialProjectId } from '@web/common/ExternalConfig/getters'

defineProps({
  showAgreement: {
    type: Boolean,
    default: true
  },
  showSupport: {
    type: Boolean,
    default: true
  }
})

defineOptions({
  name: 'AuthLayoutV3'
})

const projectId = computed(() => specialProjectId())
const componentLogo = computed(() => {
  if (projectId.value === 'take') {
    return TakeLogo
  }
  return AnyLogo
})

const isShowSupportModal = ref(false)

const termsAgreementLink = computed(() => getBaseUrl('/docs/terms_of_use.pdf'))
const personalDataAgreementLink = computed(() => getBaseUrl('/docs/personal_data_agreement.pdf'))

const handlerSupportButton = () => {
  isShowSupportModal.value = true
}
</script>

<style lang="scss" scoped>
.authLayout {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100vw;
  background-color: var(--ac-color-background);
  font-family: Inter;
  position: relative;

  &_wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    background-color: var(--ac-color-white);
    max-width: 408px;
    border-radius: 22px;
    padding: 32px 24px;
    z-index: 2;

    @media (max-width: 768px) {
      max-width: 100%;
      border-radius: 0;
      padding: 20px;
      height: 100%;
      border-radius: 0;
    }
  }

  &_preHeader {
    width: 100%;

    &:empty {
      display: none;
    }
  }

  &_header {
    color: var(--ac-color-deep-navy-blue);
    margin-bottom: 34px;
    width: 100%;

    &_logo {
      text-align: center;
      margin-bottom: 28px;

      svg {
        width: 108px;
        height: 24px;
      }

      @media (max-width: 768px) {
        text-align: left;
        margin-bottom: 22px;

        svg {
          width: 138px;
          height: 30px;
        }
      }
    }

    &_title {
      text-align: center;
      font-weight: 500;
      font-size: 24px;
      line-height: 100%;
      margin-bottom: 6px;

      @media (max-width: 768px) {
        text-align: left;
        margin-bottom: 10px;
        font-size: 22px;
        line-height: 28px;
      }
    }

    &_description {
      text-align: center;
      font-weight: 400;
      font-size: 14px;
      line-height: 17px;

      @media (max-width: 768px) {
        text-align: left;
      }
    }
  }

  &_content {
    width: 100%;
  }

  &_agreement {
    color: var(--ac-color-gray);
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0%;
    text-align: center;
    margin-top: 18px;

    &_link {
      color: var(--ac-color-gray);
    }

  }

  &_support {
    position: fixed;
    bottom: 38px;
    right: 38px;
    z-index: 2;

    font-weight: 500;
    font-size: 14px;
    line-height: 17px;
    text-align: center;
    color: var(--ac-color-black);

    &.-mobile {
      position: relative;
      right: unset;
      bottom: unset;
      margin-top: 16px;
    }

    &_link {
      color: var(--ac-color-liliac-accent);
      cursor: pointer;
      text-decoration: underline;
    }
  }
}
</style>
